import * as rudderstack from "@utils/rudderstack";
import { addBreadcrumb } from "@utils/sentry";
import React, { forwardRef, useContext } from "react";
import {
  GestureResponderEvent,
  Keyboard,
  Pressable,
  PressableProps,
  StyleSheet,
  View,
  ViewStyle,
  Platform,
} from "react-native";
import { DragContext } from "@utils/DragContext";
import type { EventMap } from "@utils/rudderstack/types";

export interface LPressableProps extends PressableProps {
  style?: ViewStyle;
  fadeEnabled?: boolean;
  sentryEventName: string | null;
  /** The press's analytics beside its name: the purchase's listing for My
   *  Tickets' actions (`listing_id`). */
  analytics?: Omit<EventMap["press"], "value">;
  shouldDismissKeyboard?: boolean;
}

//wrap LPressable in forwardRef
const LPressable = forwardRef<View, LPressableProps>(
  function LPressableComp(props, ref) {
    const {
      fadeEnabled,
      style,
      children,
      onPress,
      sentryEventName,
      analytics,
      shouldDismissKeyboard = true,
      ...rest
    } = props;

    // Always call useContext, but only use it on web
    const dragContext = useContext(DragContext);

    const stl = style ?? {};
    const internalOnPress = (e: GestureResponderEvent) => {
      // Prevent press if we just finished dragging on web
      if (Platform.OS === "web" && dragContext?.hasDragged) {
        return;
      }

      shouldDismissKeyboard && Keyboard.dismiss();
      if (sentryEventName) {
        addBreadcrumb({
          category: "ui.press",
          message: sentryEventName,
          level: "info",
          data: { value: sentryEventName, ...analytics },
        });
        void rudderstack.track("press", { value: sentryEventName, ...analytics });
      }
      onPress?.(e);
    };
    return (
      <Pressable
        ref={ref}
        {...rest}
        onPress={internalOnPress}
        style={(x) =>
          StyleSheet.flatten([
            { opacity: fadeEnabled && x.pressed ? 0.75 : 1 },
            stl,
          ])
        }
      >
        {children}
      </Pressable>
    );
  },
);

export default LPressable;
